import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Indicator, IndicatorType, IndicatorSettings } from '../chart.interface';

@Injectable({
  providedIn: 'root'
})
export class IndicatorService {
  private indicators = new BehaviorSubject<Indicator[]>([]);
  
  // Observable for components to subscribe to
  public indicators$ = this.indicators.asObservable();

  // Default colors for indicators
  private defaultColors: Record<IndicatorType, string> = {
    moving_average: '#2962FF',
    atr: '#FF6D00', 
    rsi: '#9C27B0',
    macd: '#4CAF50',
    bollinger_bands: '#FF5722',
    stochastic: '#795548'
  };

  // Default settings for each indicator type
  private defaultSettings: Record<IndicatorType, IndicatorSettings> = {
    moving_average: {
      period: 50,
      source: 'close',
      movingAverageType: 'exponential',
      /** 1–4; thin default so MAs sit lighter than candles (TradingView/Yahoo-like). */
      lineWidth: 1
    },
    atr: {
      period: 14,
      source: 'hlc3',
      lineWidth: 1
    },
    rsi: {
      period: 14,
      source: 'close',
      overboughtLevel: 70,
      oversoldLevel: 30,
      showZones: true,
      lineWidth: 1
    },
    macd: {
      fastPeriod: 12,
      slowPeriod: 26,
      signalPeriod: 9,
      source: 'close',
      lineWidth: 1
    },
    bollinger_bands: {
      period: 20,
      standardDeviations: 2,
      source: 'close',
      lineWidth: 1
    },
    stochastic: {
      kPeriod: 14,
      dPeriod: 3,
      smooth: 3,
      lineWidth: 1
    }
  };

  constructor() {}

  // Get all indicators
  getIndicators(): Indicator[] {
    return this.indicators.value;
  }

  // Add a new indicator
  addIndicator(type: IndicatorType, customSettings?: Partial<IndicatorSettings>): string {
    try {
      // Null safety check for type
      if (!type || typeof type !== 'string') {
        console.warn('[WARNING] Invalid indicator type provided to addIndicator');
        return '';
      }

      // Validate that the type exists in our default settings
      if (!this.defaultSettings[type]) {
        console.warn(`[WARNING] Unknown indicator type: ${type}`);
        return '';
      }

      const id = `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      let effectiveCustomSettings = customSettings;

      // Special handling for Moving Average defaults: cycle 50 / 100 / 200
      if (type === 'moving_average' && (!customSettings || customSettings.period === undefined)) {
        const existingMAs = this.getIndicatorsByType('moving_average');
        const existingPeriods = existingMAs.map(ma => ma.settings.period).filter(Boolean) as number[];
        const preferred = [50, 100, 200];
        const nextPeriod = preferred.find(p => !existingPeriods.includes(p)) || 50;

        effectiveCustomSettings = {
          ...(customSettings || {}),
          period: nextPeriod
        };
      }

      const settings: IndicatorSettings = {
        ...this.defaultSettings[type],
        ...(effectiveCustomSettings && typeof effectiveCustomSettings === 'object' ? effectiveCustomSettings : {})
      };

      // Determine color (override for specific MA periods)
      let color = this.defaultColors[type] || '#2962FF';
      if (type === 'moving_average' && typeof settings.period === 'number') {
        const periodColorMap: Record<number, string> = {
          50: '#4CAF50',   // Green
          100: '#FFC107',  // Yellow / Orange
          200: '#F44336'   // Red
        };
        color = periodColorMap[settings.period] || color;
      }

      const indicator: Indicator = {
        id,
        name: this.getIndicatorDisplayName(type, settings),
        type,
        visible: true,
        settings,
        color,
        createdAt: new Date(),
        isFavorite: false
      };

      const currentIndicators = this.indicators.value;
      this.indicators.next([...currentIndicators, indicator]);
      
      return id;
    } catch (error) {
      console.error('[ERROR] Error adding indicator:', error);
      return '';
    }
  }

  // Update an existing indicator
  updateIndicator(id: string, updates: Partial<Indicator>): boolean {
    // Null safety check for id
    if (!id || typeof id !== 'string' || id.trim().length === 0) {
      console.warn('[WARNING] Invalid indicator ID provided to updateIndicator');
      return false;
    }

    // Null safety check for updates
    if (!updates || typeof updates !== 'object') {
      console.warn('[WARNING] Invalid updates object provided to updateIndicator');
      return false;
    }

    const currentIndicators = this.indicators.value;
    const index = currentIndicators.findIndex(indicator => indicator && indicator.id === id);
    
    if (index === -1) {
      console.warn(`[WARNING] Indicator with ID '${id}' not found`);
      return false;
    }

    const existingIndicator = currentIndicators[index];
    if (!existingIndicator) {
      console.warn(`[WARNING] Invalid indicator found at index ${index}`);
      return false;
    }

    const updatedIndicator = { ...existingIndicator, ...updates };
    
    // Merge settings deeply and ensure numeric fields (period, etc.) are numbers
    if (updates.settings && typeof updates.settings === 'object') {
      updatedIndicator.settings = { ...existingIndicator.settings, ...updates.settings };
      const s = updatedIndicator.settings as Record<string, unknown>;
      if (s['period'] !== undefined) s['period'] = Math.round(Number(s['period']) || 50);
      if (s['fastPeriod'] !== undefined) s['fastPeriod'] = Math.round(Number(s['fastPeriod']) || 12);
      if (s['slowPeriod'] !== undefined) s['slowPeriod'] = Math.round(Number(s['slowPeriod']) || 26);
      if (s['signalPeriod'] !== undefined) s['signalPeriod'] = Math.round(Number(s['signalPeriod']) || 9);
      if (s['kPeriod'] !== undefined) s['kPeriod'] = Math.round(Number(s['kPeriod']) || 14);
      if (s['dPeriod'] !== undefined) s['dPeriod'] = Math.round(Number(s['dPeriod']) || 3);
      if (s['smooth'] !== undefined) s['smooth'] = Math.round(Number(s['smooth']) || 3);
      if (s['standardDeviations'] !== undefined) s['standardDeviations'] = Number(s['standardDeviations']) || 2;
      if (s['overboughtLevel'] !== undefined) s['overboughtLevel'] = Math.round(Number(s['overboughtLevel']) || 70);
      if (s['oversoldLevel'] !== undefined) s['oversoldLevel'] = Math.round(Number(s['oversoldLevel']) || 30);
      if (s['lineWidth'] !== undefined) {
        const lw = Math.round(Number(s['lineWidth']));
        s['lineWidth'] = Number.isFinite(lw) ? Math.min(4, Math.max(1, lw)) : 1;
      }
      updatedIndicator.name = this.getIndicatorDisplayName(updatedIndicator.type, updatedIndicator.settings);
    }

    const updatedIndicators = [...currentIndicators];
    updatedIndicators[index] = updatedIndicator;
    
    this.indicators.next(updatedIndicators);
    return true;
  }

  // Remove an indicator
  removeIndicator(id: string): boolean {
    const currentIndicators = this.indicators.value;
    const filteredIndicators = currentIndicators.filter(indicator => indicator.id !== id);
    
    if (filteredIndicators.length === currentIndicators.length) return false;
    
    this.indicators.next(filteredIndicators);
    return true;
  }

  // Toggle indicator visibility
  toggleIndicatorVisibility(id: string): boolean {
    const currentIndicators = this.indicators.value;
    const indicator = currentIndicators.find(ind => ind.id === id);
    
    if (!indicator) return false;
    
    return this.updateIndicator(id, { visible: !indicator.visible });
  }

  // Get indicator by ID
  getIndicatorById(id: string): Indicator | undefined {
    return this.indicators.value.find(indicator => indicator.id === id);
  }

  // Get indicators by type
  getIndicatorsByType(type: IndicatorType): Indicator[] {
    return this.indicators.value.filter(indicator => indicator.type === type);
  }

  /** Maximum lookback bars required by currently visible indicators (for EMA warm-up). */
  getMaxLookbackPeriod(): number {
    const indicators = this.getIndicators().filter(indicator => indicator.visible);
    let maxLookback = 0;

    for (const indicator of indicators) {
      const settings = indicator.settings;
      switch (indicator.type) {
        case 'moving_average':
        case 'atr':
        case 'rsi':
        case 'bollinger_bands':
          maxLookback = Math.max(maxLookback, Math.round(Number(settings.period) || 0));
          break;
        case 'macd':
          maxLookback = Math.max(
            maxLookback,
            Math.round(Number(settings['slowPeriod']) || 0) + Math.round(Number(settings['signalPeriod']) || 0)
          );
          break;
        case 'stochastic':
          maxLookback = Math.max(
            maxLookback,
            Math.round(Number(settings['kPeriod']) || 0) + Math.round(Number(settings['smooth']) || 0)
          );
          break;
      }
    }

    return maxLookback;
  }

  // Generate display name for indicator
  private getIndicatorDisplayName(type: IndicatorType, settings: IndicatorSettings): string {
    switch (type) {
      case 'moving_average':
        const maType = (settings.movingAverageType || 'exponential') === 'exponential' ? 'EMA' : 'SMA';
        return `${maType}(${settings.period})`;
      case 'atr':
        return `ATR(${settings.period})`;
      case 'rsi':
        return `RSI(${settings.period})`;
      case 'macd':
        return `MACD(${settings['fastPeriod']},${settings['slowPeriod']},${settings['signalPeriod']})`;
      case 'bollinger_bands':
        return `BB(${settings.period},${settings['standardDeviations']})`;
      case 'stochastic':
        return `Stoch(${settings['kPeriod']},${settings['dPeriod']})`;
      default:
        return String(type).toUpperCase();
    }
  }

  // Get available indicator types with display names
  getAvailableIndicatorTypes(): { type: IndicatorType; displayName: string; description: string }[] {
    return [
      {
        type: 'moving_average',
        displayName: 'Moving Average',
        description: 'Simple or Exponential Moving Average'
      },
      {
        type: 'atr',
        displayName: 'ATR',
        description: 'Average True Range'
      },
      {
        type: 'rsi',
        displayName: 'RSI',
        description: 'Relative Strength Index'
      },
      {
        type: 'macd',
        displayName: 'MACD',
        description: 'Moving Average Convergence Divergence'
      },
      {
        type: 'bollinger_bands',
        displayName: 'Bollinger Bands',
        description: 'Bollinger Bands'
      },
      {
        type: 'stochastic',
        displayName: 'Stochastic',
        description: 'Stochastic Oscillator'
      }
    ];
  }

  // Clear all indicators
  clearAllIndicators(): void {
    this.indicators.next([]);
  }

  // Load indicators from storage/state
  loadIndicators(indicators: Indicator[]): void {
    this.indicators.next(indicators);
  }

  // Toggle favorite status of an indicator
  toggleIndicatorFavorite(id: string): boolean {
    const currentIndicators = this.indicators.value;
    const indicator = currentIndicators.find(ind => ind.id === id);
    
    if (!indicator) return false;
    
    return this.updateIndicator(id, { isFavorite: !indicator.isFavorite });
  }

  // Get favorite indicators
  getFavoriteIndicators(): Indicator[] {
    return this.indicators.value.filter(indicator => indicator.isFavorite === true);
  }

  // Get indicators by favorite status
  getIndicatorsByFavorite(isFavorite: boolean): Indicator[] {
    return this.indicators.value.filter(indicator => indicator.isFavorite === isFavorite);
  }
}
